import { useState } from 'react';
import {
  Card, Button, Badge, Notice, useToast,
} from '@kollez/design-system';
import { useOcrMarksheet, useConfirmMarksheet, type OcrResult, type OcrSubject } from '@/lib/queries';
import { apiPost } from '@/lib/api';

/**
 * /student/upload-marks (F71) — 3-step marksheet upload flow:
 *   1. Pick a PDF/image
 *   2. Nexie OCR extracts subjects + SGPA + CGPA (Nexie confidence surfaced)
 *   3. Student edits the diff, then confirms to write into their profile
 */
export function StudentUploadMarksPage() {
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<'pick' | 'review' | 'done'>('pick');
  const [ocr, setOcr] = useState<OcrResult | null>(null);
  const [subjects, setSubjects] = useState<OcrSubject[]>([]);
  const [semester, setSemester] = useState<number>(1);
  const [sgpa, setSgpa] = useState<string>('');
  const [cgpa, setCgpa] = useState<string>('');

  const ocrMutation = useOcrMarksheet();
  const confirm = useConfirmMarksheet();
  const toast = useToast();

  async function runOcr() {
    if (!file) { toast.show({ tone: 'warning', message: 'Pick a marksheet first.' }); return; }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await apiPost<{ url: string; path: string }>('/uploads/marksheet', fd, { headers: { 'Content-Type': undefined as unknown as string } });
      const r = await ocrMutation.mutateAsync({ imageUrl: up.url });
      setOcr(r.ocr);
      setSubjects(r.ocr.subjects);
      setSemester(r.ocr.semester || 1);
      setSgpa(r.ocr.sgpa != null ? String(r.ocr.sgpa) : '');
      setCgpa(r.ocr.cgpa != null ? String(r.ocr.cgpa) : '');
      setStep('review');
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Upload / OCR failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  async function submit() {
    try {
      await confirm.mutateAsync({
        semester,
        sgpa: sgpa ? Number(sgpa) : undefined,
        cgpa: cgpa ? Number(cgpa) : undefined,
        totalCredits: ocr?.totalCredits,
        subjects,
      });
      toast.show({ message: 'Marks saved to your profile.' });
      setStep('done');
    } catch {
      toast.show({ tone: 'warning', message: 'Could not save marks.' });
    }
  }

  function editSubject(i: number, patch: Partial<OcrSubject>) {
    setSubjects((prev) => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  }
  function removeSubject(i: number) {
    setSubjects((prev) => prev.filter((_, idx) => idx !== i));
  }
  function addSubject() {
    setSubjects((prev) => [...prev, { code: '', name: '', credits: 3, grade: 'A' }]);
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Upload marks</p>
          <h1 className="h-page">Nexie reads your marksheet</h1>
          <p className="subhead">Snap the sheet or upload a PDF. Nexie extracts subjects + SGPA — you check and confirm before it writes anything.</p>
        </div>
      </div>

      {step === 'pick' && (
        <Card padding="lg">
          <div className="grid gap-3">
            <label className="grid gap-1">
              <span className="text-xs uppercase tracking-wide text-ink-tertiary">Marksheet (PDF or image)</span>
              <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              {file && <span className="text-xs text-ink-tertiary">{file.name} · {(file.size / 1024).toFixed(0)} KB</span>}
            </label>
            <div>
              <Button variant="primary" onClick={runOcr} loading={ocrMutation.isPending}>Extract with Nexie</Button>
            </div>
          </div>
          <Notice tone="info" className="mt-4">
            {/* Deep-audit fix (Page 27/N): the old copy claimed the file
                'stays private in your document vault'. But the upload
                lands in blob storage; nothing creates a Document row
                linked to /documents. Corrected the claim: the file is
                stored privately (per-student path, tenant-scoped), and
                only the extracted numbers, once the student confirms,
                are written to the profile. */}
            Nexie only reads; it never writes without your OK. Your original file is stored privately, and only the numbers you confirm below are written to your profile.
          </Notice>
        </Card>
      )}

      {step === 'review' && ocr && (
        <>
          <Card padding="lg">
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <h2 className="h-section">Review + confirm</h2>
              <Badge tone={ocr.confidence === 'hi' ? 'success' : 'warning'}>{ocr.confidence === 'hi' ? 'High confidence' : 'Low confidence — check carefully'}</Badge>
            </div>
            {ocr.warnings.length > 0 && (
              <Notice tone="warning" className="mb-3">
                Nexie flagged: {ocr.warnings.join(' · ')}
              </Notice>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <label className="grid gap-1">
                <span className="text-xs uppercase tracking-wide text-ink-tertiary">Semester</span>
                <input type="number" min={1} max={12} value={semester} onChange={(e) => setSemester(Number(e.target.value))} className="input" />
              </label>
              <label className="grid gap-1">
                <span className="text-xs uppercase tracking-wide text-ink-tertiary">SGPA</span>
                <input type="number" step="0.01" value={sgpa} onChange={(e) => setSgpa(e.target.value)} className="input" />
              </label>
              <label className="grid gap-1">
                <span className="text-xs uppercase tracking-wide text-ink-tertiary">CGPA (cumulative)</span>
                <input type="number" step="0.01" value={cgpa} onChange={(e) => setCgpa(e.target.value)} className="input" />
              </label>
            </div>

            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-ink-tertiary">
                <tr>
                  <th className="text-left py-2">Code</th>
                  <th className="text-left">Name</th>
                  <th className="text-right">Credits</th>
                  <th className="text-right">Grade</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {subjects.map((s, i) => (
                  <tr key={i} className="border-t border-line-primary">
                    <td><input value={s.code} onChange={(e) => editSubject(i, { code: e.target.value })} className="input" /></td>
                    <td><input value={s.name} onChange={(e) => editSubject(i, { name: e.target.value })} className="input" /></td>
                    <td className="text-right"><input type="number" step="0.5" value={s.credits} onChange={(e) => editSubject(i, { credits: Number(e.target.value) })} className="input text-right w-20" /></td>
                    <td className="text-right"><input value={s.grade} onChange={(e) => editSubject(i, { grade: e.target.value })} className="input text-right w-16" /></td>
                    <td className="text-right"><Button variant="ghost" onClick={() => removeSubject(i)}>×</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between mt-3">
              <Button variant="ghost" onClick={addSubject}>+ Add subject</Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setStep('pick')}>Discard</Button>
                <Button variant="primary" onClick={submit} loading={confirm.isPending}>Save to profile</Button>
              </div>
            </div>
          </Card>
        </>
      )}

      {step === 'done' && (
        <Card padding="lg">
          <h2 className="h-section">Saved ✓</h2>
          {/* Deep-audit fix (Page 27/N): the useConfirmMarksheet hook
              invalidates ['student','profile-360'] on success, which means
              the readiness score refreshes as soon as the student opens
              /profile-360 — not 'on next visit'. Corrected the copy so
              it doesn't understate what the app is doing. */}
          <p className="text-ink-secondary mt-2">Your semester marks are on file. Your readiness score has been queued to recompute.</p>
          <div className="mt-3"><Button variant="primary" onClick={() => { setStep('pick'); setFile(null); setOcr(null); }}>Upload another semester</Button></div>
        </Card>
      )}
    </div>
  );
}
